import React, { useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'
import { isEmpty, isString } from 'lodash'

import queryString from 'query-string'
import MyhouseAuthType from '@Components/modules/MyhouseAuthType'
import { MYHOUSE_AUTHENTICATION_CATEGORY_TYPE, MYHOUSE_AUTHENTICATION_TYPE, MYHOUSE_REGISTER_CALLBACK_URL_KEY, MYHOUSE_REGISTER_CALLBACK_URL_PARAM, MYHOUSE_REGISTER_PAGE } from '@Root/contants/myhouseRegister'
import useAuthentication from '@Hooks/myhouse/useAuthentication'
import MyhouseAuthOwnerMethod from '@Components/atoms/MyhouseAuthOwnerMethod'
import MyhouseAuthResidentMethod from '@Components/atoms/MyhouseAuthResidentMethod'
import MyhouseAuthHeader from '@Components/atoms/MyhouseAuthHeader'
import MyhouseAuthProgress from '@Components/atoms/MyhouseAuthProgress'
import useBrowser from '@Hooks/myhouse/useBrowser'
import useModal from '@Hooks/myhouse/useModal'
import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import { AUTH_STATUS, FETCH_STATUS, TRADE } from '@Root/contants'
import useMyhouse from '@Hooks/myhouse/useMyhouse'
import { AUTHENTICATION_MAX_COUNT } from '@Root/contants/myhouse'
import { stringListJoinAfterTrim } from '@Root/utils/articleUtils'
import { handleNclick, handleNclickMix } from '@Helpers/nclick'
import { NCLICK } from '@Root/contants/lcs'
import useDebounce from '@Hooks/common/useDebounce'
import { getDeniedPath, LAND_DENIED_TYPE } from '@Root/contants/myhouseError'
import { useSessionStorage } from '@Root/utils/storageUtils'
import useSetAuthCallbackUrl from '@Root/hooks/myhouse/useSetAuthCallbackUrl'

export interface MyhouseAuthParam {
  myhouseNo: string
  complexNo: string
  categoryParam: string
  typeParam: string
}

const MyhouseAuth: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const { isMobile, isIpad } = useBrowser()
  const { modal, open, clean } = useModal()
  const { myhouseNo, complexNo, categoryParam, typeParam } = useParams<MyhouseAuthParam>()
  const { landDebounce } = useDebounce()
  const [categoryType, setCategoryType] = useState<MYHOUSE_AUTHENTICATION_CATEGORY_TYPE>()
  const { myhouse, isLoaded } = useMyhouse()
  const { authComplexNoMap } = myhouse
  const { authentication, loadAuthentication } = useAuthentication()
  const { ownerAuthCnt, residentAuthCnt } = authentication
  const [type, setType] = useState<MYHOUSE_AUTHENTICATION_TYPE>(MYHOUSE_AUTHENTICATION_TYPE.NONE)
  const [isShowLoading, setIsShowLoading] = useState(true)
  const debouncedDisableLoading = landDebounce(() => {
    setIsShowLoading(false)
  }, 300)
  const { getAndRemoveStorage } = useSessionStorage();
  useSetAuthCallbackUrl();

  const changeCategoryTypeHistory = (categoryType: MYHOUSE_AUTHENTICATION_CATEGORY_TYPE) => {
    if (categoryType === MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.NONE) {
      history.push(`${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${myhouseNo}`)
    } else {
      history.push(`${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${myhouseNo}/${categoryType}`)
    }
  }

  const changeTypeHistory = (type: MYHOUSE_AUTHENTICATION_TYPE) => {
    if (type === MYHOUSE_AUTHENTICATION_TYPE.NONE) {
      window.location.href = `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${myhouseNo}`
    } else if (type === MYHOUSE_AUTHENTICATION_TYPE.CERTIFICATE_OF_REALESTATE_REGISTER) {
      window.location.href = `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${myhouseNo}/${MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.OWNER}/${type}`
    } else {
      window.location.href = `${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${myhouseNo}/${MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.RESIDENT}/${type}`
    }
  }

  useEffect(() => {
    // 인증 용도의 미디어 뷰어가 열린 상태에서 새로고침한 경우 #0으로 pushState 되어있는 경우 뒤로가기
    // 직접 접근시 뒤로가기가 불가능하므로 해시 제거
    if (window.location.hash) {
      window.history.back()
      window.history.replaceState(null, '', '#')
    }

    debouncedDisableLoading()
  }, [])

  useEffect(() => {
    if (isLoaded) {
      if (complexNo) {
        if (authComplexNoMap[complexNo] && authComplexNoMap[complexNo]?.[0]?.houseNo) {
          history.replace(`/myhouse/auth/${authComplexNoMap[complexNo][0].houseNo}`)
        } else {
          history.replace(`${MYHOUSE_REGISTER_PAGE.ADDRESS_SEARCH}?complexNo=${complexNo}`)
        }
      } else if (myhouseNo) {
        loadAuthentication(Number(myhouseNo))
      } else {
        history.replace('/myhouse')
      }
    }
  }, [complexNo, isLoaded])

  useEffect(() => {
    if (!myhouseNo) {
      return
    }

    if (authentication.fetchStatus === FETCH_STATUS.FAILURE) {
      // user, houseNo match failed.
      history.replace(getDeniedPath(LAND_DENIED_TYPE.AUTH_LOAD_FAIL))
      return
    }

    if (authentication.fetchStatus === FETCH_STATUS.SUCCESS && !authentication.houseNo) {
      history.replace(getDeniedPath(LAND_DENIED_TYPE.AUTH_LOAD_FAIL))
      return
    }

    if (authentication.fetchStatus === FETCH_STATUS.SUCCESS && isLoaded) {
      const progressAuthStatus = [AUTH_STATUS.APPLY1, AUTH_STATUS.APPLY2]

      // 인증 진행 중 접근 불가 처리
      if (progressAuthStatus.includes(authentication.ownerAuthStatus as AUTH_STATUS) || progressAuthStatus.includes(authentication.residentAuthStatus as AUTH_STATUS)) {
        history.replace(getDeniedPath(LAND_DENIED_TYPE.AUTH_PROGRESS))
      }

      let categoryType: MYHOUSE_AUTHENTICATION_CATEGORY_TYPE = MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.NONE
      if (authentication.tradeTypeCode === TRADE.DEAL) {
        if (authentication.ownerAuth === 'Y') {
          history.replace(getDeniedPath(LAND_DENIED_TYPE.AUTH_DONE))
        }

        // 분양권 집주인 인증 제외.
        if (['B01', 'B02', 'B03'].includes(authentication.realestateTypeCode)) {
          categoryType = MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.RESIDENT
        } else {
          categoryType = MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.OWNER
        }
      } else {
        categoryType = MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.RESIDENT
      }

      categoryType = (categoryParam as MYHOUSE_AUTHENTICATION_CATEGORY_TYPE) || categoryType
      setCategoryType(categoryType)

      const type: MYHOUSE_AUTHENTICATION_TYPE = typeParam as MYHOUSE_AUTHENTICATION_TYPE
      setType(type || MYHOUSE_AUTHENTICATION_TYPE.NONE)
    }
  }, [authentication.fetchStatus, isLoaded, location])

  useEffect(() => {
    if (
      (authentication?.tradeTypeCode === TRADE.DEAL && ownerAuthCnt === AUTHENTICATION_MAX_COUNT && residentAuthCnt === AUTHENTICATION_MAX_COUNT) ||
      (authentication?.tradeTypeCode !== TRADE.DEAL && residentAuthCnt === AUTHENTICATION_MAX_COUNT)
    ) {
      history.replace(getDeniedPath(LAND_DENIED_TYPE.AUTH_MAX_COUNT_OVER))
    }
  }, [categoryType])

  useEffect(() => {
    if (modal.confirmModal === MODAL_TYPE.AUTH_REALNAME_GUIDE) {
      clean()
      const successReturnUrl = window.location.href
      const failedReturnUrl = `${process.env.REACT_APP_LAND_WEB_URL}/myhouse`
      if (isMobile || isIpad) {
        window.location.href = `https://nid.naver.com/mobile/user/help/realNameCheck?type=2&rurl=${successReturnUrl}&surl=${failedReturnUrl}`
      } else {
        window.location.href = `https://nid.naver.com/user2/help/realNameCheck?type=2&rurl=${successReturnUrl}&surl=${failedReturnUrl}`
      }
    }
  }, [modal.confirmModal])

  useEffect(() => {
    if (modal.cancelModal === MODAL_TYPE.AUTH_REALNAME_GUIDE) {
      history.push(`${LAND_SERVICE_PREFIX_URL.MYHOUSE}/auth/${myhouseNo}`)
    }
  }, [modal.cancelModal])

  const handleCloseClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    const authCallbackUrl = getAndRemoveStorage(MYHOUSE_REGISTER_CALLBACK_URL_KEY);

    if (authCallbackUrl) {
      window.location.href = authCallbackUrl;
    } else if (window.location.pathname.includes('myasset')) {
      window.location.href = `${process.env.REACT_APP_NAVER_MYMONEY_URL}`
    } else {
      window.location.href = `/myhouse/${myhouseNo}`
    }
  }

  const handleResidentAuthClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    handleNclick(NCLICK.MY_HOUSE.AUTHENTICATION.SELECT_AUTH_TYPE.RESIDENT, e)

    changeCategoryTypeHistory(MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.RESIDENT)
  }

  const handleCertificationAuthClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    handleNclick(NCLICK.MY_HOUSE.AUTHENTICATION.SELECT_AUTH_TYPE.OWNER, e)

    if (ownerAuthCnt <= 2) {
      changeTypeHistory(MYHOUSE_AUTHENTICATION_TYPE.CERTIFICATE_OF_REALESTATE_REGISTER)
    }
  }

  const handleMaintainFeeAuthClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    handleNclick(NCLICK.MY_HOUSE.AUTHENTICATION.SELECT_RESIDENT_AUTH_TYPE.FEE, e)

    if (isMobile || isIpad) {
      changeTypeHistory(MYHOUSE_AUTHENTICATION_TYPE.MANAGEMENT_FEE)
    } else {
      open(MODAL_TYPE.AUTH_MOBILE_GUIDE, {
        nclickCancel: NCLICK.MY_HOUSE.AUTHENTICATION.SELECT_RESIDENT_AUTH_TYPE.MOBILE,
      })
    }
  }

  const handlePackageAuthClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    handleNclick(NCLICK.MY_HOUSE.AUTHENTICATION.SELECT_RESIDENT_AUTH_TYPE.PACKAGE, e)

    if (isMobile || isIpad) {
      changeTypeHistory(MYHOUSE_AUTHENTICATION_TYPE.PACKAGE)
    } else {
      open(MODAL_TYPE.AUTH_MOBILE_GUIDE)
    }
  }

  const myHouseExtraAddress = stringListJoinAfterTrim([
    authentication.complexName,
    authentication.buildName ? `${authentication.buildName}동` : '',
    authentication.hoName ? `${authentication.hoName}호` : '',
  ])

  if (isShowLoading) {
    return <MyhouseAuthProgress showGuide={false} />
  }

  if (categoryType && !isEmpty(type)) {
    return <MyhouseAuthType categoryType={categoryType} type={type} onCloseClick={handleCloseClick} ownerAuthCount={ownerAuthCnt} residentAuthCount={residentAuthCnt} />
  }
  switch (categoryType) {
    case MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.OWNER:
      return (
        <>
          <MyhouseAuthHeader onCloseClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.SELECT_AUTH_TYPE.CLOSE, handleCloseClick)} />
          <MyhouseAuthOwnerMethod
            ownerAuthCount={ownerAuthCnt}
            residentAuthCount={residentAuthCnt}
            onOwnerAuthClick={handleCertificationAuthClick}
            onResidentAuthClick={handleResidentAuthClick}
            myHouseBaseAddress={authentication.baseAddress}
            myHouseExtraAddress={myHouseExtraAddress}
          />
        </>
      )
    case MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.RESIDENT:
      return (
        <>
          <MyhouseAuthHeader onCloseClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.SELECT_RESIDENT_AUTH_TYPE.CLOSE, handleCloseClick)} />
          <MyhouseAuthResidentMethod
            residentAuthCount={residentAuthCnt}
            onMaintainFeeAuthClick={handleMaintainFeeAuthClick}
            onPackageAuthClick={handlePackageAuthClick}
            realestateTypeCode={authentication?.realestateTypeCode}
          />
        </>
      )
    default:
      return <MyhouseAuthProgress showGuide={false} />
  }
}
export default MyhouseAuth
